// ============================================================================
// WorkbenchEngagementBrief.jsx
// Engagement Brief form — standalone component.
// ============================================================================

import React from 'react';
import { Users } from 'lucide-react';
import * as S from '@process-advisor/process-miner/consultant/workbenchStyles';

// ── Field components ────────────────────────────────────────────────────────

const Field = ({ label, value, onChange, placeholder = '', rows = 1, style = {} }) => (
  <label style={{ ...S.FIELD, ...style }}>
    <span style={S.FIELD_LABEL}>{label}</span>
    {rows > 1
      ? <textarea style={S.TEXTAREA} value={value || ''} placeholder={placeholder} rows={rows} onChange={(e) => onChange?.(e.target.value)} />
      : <input    style={S.INPUT}    value={value || ''} placeholder={placeholder}             onChange={(e) => onChange?.(e.target.value)} />
    }
  </label>
);

// ── Component ───────────────────────────────────────────────────────────────

const WorkbenchEngagementBrief = ({ workspace = {}, onUpdate, processLabel = 'Process' }) => {
  const update = (patch) => onUpdate?.(patch);

  return (
    <div style={S.ROOT}>
      <div style={S.SHELL}>
        <div style={S.CARD}>

          <div style={S.CARD_HEADER}>
            <div style={S.CARD_TITLE_ROW}>
              <Users size={17} color={S.C.B400} />
              <div style={S.CARD_TITLE}>Engagement Brief</div>
            </div>
          </div>

          <div style={S.CARD_BODY}>
            <div style={{ ...S.DIVIDER_NOTE, marginBottom: 12 }}>
              Capture the context for this engagement: what is in scope, who owns the process, and what the client or team is expecting from the analysis.
            </div>

            <div style={S.FORM_GRID}>
              <Field
                style={{ gridColumn: '1 / -1' }}
                label="Objective"
                value={workspace.objective}
                onChange={(v) => update({ objective: v })}
                placeholder={`e.g. Assess why this ${processLabel.toLowerCase()} is slow, costly, or breaching SLA`}
                rows={2}
              />
              <Field
                style={{ gridColumn: '1 / -1' }}
                label="Scope"
                value={workspace.scope}
                onChange={(v) => update({ scope: v })}
                placeholder="Projects, issue types, date range, exclusions, teams in scope"
                rows={2}
              />
              <Field
                label="Business area / team"
                value={workspace.businessArea}
                onChange={(v) => update({ businessArea: v })}
              />
              <Field
                label="Process owner"
                value={workspace.processOwner}
                onChange={(v) => update({ processOwner: v })}
              />
              <Field
                style={{ gridColumn: '1 / -1' }}
                label="Stakeholders"
                value={workspace.stakeholders}
                onChange={(v) => update({ stakeholders: v })}
                placeholder="Teams, client contacts, delivery stakeholders"
                rows={2}
              />
              <Field
                label="Target SLA / service expectation"
                value={workspace.targetSla}
                onChange={(v) => update({ targetSla: v })}
              />
              <Field
                label="Known pain points"
                value={workspace.painPoints}
                onChange={(v) => update({ painPoints: v })}
              />
              <Field
                style={{ gridColumn: '1 / -1' }}
                label="Working hypothesis"
                value={workspace.workingHypothesis}
                onChange={(v) => update({ workingHypothesis: v })}
                placeholder="What you suspect before reviewing the full evidence"
                rows={2}
              />
              <Field
                style={{ gridColumn: '1 / -1' }}
                label="Engagement notes"
                value={workspace.notes}
                onChange={(v) => update({ notes: v })}
                placeholder="Assumptions, decisions, risks, open questions"
                rows={3}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WorkbenchEngagementBrief;
